// src/i18n/technicianStrings.js
//
// Central dictionary for all field-UI strings — every screen a technician
// or site engineer uses (D-307). Manager/Accounts/Office pages are NOT
// covered yet — they stay English-only for now.
// RULE: never type Hinglish (or English) text directly in a field screen —
// add a key here and use t("key") from useLang().
//
// Adding a new language later = add a new key (e.g. "mr" for Marathi) to
// each entry below. No component code needs to change.
//
// "hi" here means ROMAN-SCRIPT HINGLISH (e.g. "Machine Add Karo"), not
// Devanagari — this is the natural spoken/read style for field technicians
// and keeps English technical nouns (Machine, Part, Job, GPS) unchanged.

export const STRINGS = {
  // ── Defective Item Form ──────────────────────────────────────────────
  item_name_placeholder: {
    en: "Item Name *",
    hi: "Item ka Naam *",
  },
  qty_placeholder: {
    en: "Quantity",
    hi: "Quantity",
  },
  machine_details_heading: {
    en: "🏭 Machine Details",
    hi: "🏭 Machine Details",
  },
  no_machine_registered_title: {
    en: "No machine registered yet",
    hi: "Koi machine registered nahi hai",
  },
  no_machine_registered_body: {
    en: "Add a machine first, then you can fill part details.",
    hi: "Pehle machine add karo, phir Part Details bhar sakte ho.",
  },
  add_machine_button: {
    en: "+ Add Machine",
    hi: "+ Machine Add Karo",
  },
  select_machine_placeholder: {
    en: "Select Machine (registered — optional)",
    hi: "Machine Chuno (registered — optional)",
  },
  machine_description_placeholder: {
    en: "Machine Description",
    hi: "Machine ka Description",
  },
  machine_model_placeholder: {
    en: "Machine Model No.",
    hi: "Machine Model No.",
  },
  part_details_heading: {
    en: "🔩 Part Details",
    hi: "🔩 Part Details",
  },
  brand_name_placeholder: {
    en: "Brand Name",
    hi: "Brand Name",
  },
  part_code_placeholder: {
    en: "Part Code",
    hi: "Part Code",
  },
  part_serial_placeholder: {
    en: "Part Serial No.",
    hi: "Part Serial No.",
  },
  remarks_placeholder: {
    en: "Remarks",
    hi: "Remarks (jo bhi batana ho)",
  },
  save_defective_item: {
    en: "Save Defective Item",
    hi: "Defective Item Save Karo",
  },
  saving: {
    en: "Saving...",
    hi: "Save ho raha hai...",
  },
  save_failed: {
    en: "Could not save. Please try again.",
    hi: "Save nahi hua. Dubara try karo.",
  },
  fill_required_fields: {
    en: "Please fill the required fields.",
    hi: "Zaroori fields bharo.",
  },

  // ── Technician Dashboard (D-306 / D-307) ─────────────────────────────
  my_jobs:                 { en: "My Jobs",                     hi: "Mere Jobs" },
  welcome_name:            { en: "Welcome, {name} 👷",          hi: "Namaste, {name} 👷" },
  technician_fallback:     { en: "Technician",                  hi: "Technician" },
  online:                  { en: "Online",                      hi: "Online" },
  offline:                 { en: "Offline",                     hi: "Offline" },
  pending_sync:            { en: "{count} pending sync",        hi: "{count} sync baaki" },
  offline_banner:          { en: "⚡ Offline mode — Start/End Job will sync when online",
                             hi: "⚡ Offline mode — internet aane par Start/End Job sync ho jayega" },
  stat_active:             { en: "Active",                      hi: "Chalu" },
  stat_assigned:           { en: "Assigned",                    hi: "Assigned" },
  stat_pending:            { en: "Pending",                     hi: "Approval Baaki" },
  no_jobs_title:           { en: "No jobs assigned",            hi: "Koi job assign nahi hai" },
  no_jobs_body:            { en: "Check back later",            hi: "Thodi der baad check karo" },
  status_assigned:         { en: "Assigned",                    hi: "Assigned" },
  status_in_progress:      { en: "In Progress",                 hi: "Kaam Chalu" },
  status_partial:          { en: "Partial Done",                hi: "Aadha Kaam Hua" },
  status_pending_approval: { en: "Pending Approval",            hi: "Approval Baaki" },
  no_complaint:            { en: "No complaint",                hi: "Complaint nahi likhi" },
  open_map:                { en: "Open Map →",                  hi: "Map Kholo →" },
  accept_job:              { en: "✓ Accept Job",                hi: "✓ Job Accept Karo" },
  reject_job_btn:          { en: "✕ Reject",                    hi: "✕ Reject Karo" },
  start_work:              { en: "Start Work",                  hi: "Kaam Shuru Karo" },
  you_rejected_job:        { en: "✕ You rejected this job",     hi: "✕ Aapne ye job reject ki" },
  continue_job:            { en: "⚡ Continue Job",              hi: "⚡ Job Continue Karo" },
  awaiting_sm_approval:    { en: "⏳ Submitted — Awaiting SM Approval",
                             hi: "⏳ Submit ho gaya — SM approval ka intezaar" },
  reject_job_title:        { en: "Reject Job",                  hi: "Job Reject Karo" },
  reject_reason_note:      { en: "Reason is mandatory. SM will be notified.",
                             hi: "Reason likhna zaroori hai. SM ko notification jayega." },
  reject_reason_placeholder: { en: "Enter rejection reason...", hi: "Reject karne ka reason likho..." },
  cancel:                  { en: "Cancel",                      hi: "Cancel" },
  confirm_reject:          { en: "Confirm Reject",              hi: "Reject Confirm Karo" },
  reject_reason_required:  { en: "Rejection reason is required", hi: "Reject ka reason likhna zaroori hai" },
  accept_failed:           { en: "Accept failed",               hi: "Accept nahi hua" },
  reject_failed:           { en: "Reject failed",               hi: "Reject nahi hua" },

  // ── Task Detail — Complete Job + status labels (D-308) ───────────────
  issue_found_placeholder:     { en: "Issue Found (optional)",       hi: "Kya problem mili (optional)" },
  work_done_placeholder:       { en: "Work Done (optional)",         hi: "Kya kaam kiya (optional)" },
  detailed_report_placeholder: { en: "Detailed Report *",            hi: "Poori Report *" },
  detailed_report_required:    { en: "❌ Please write the detailed report", hi: "❌ Poori report likhna zaroori hai" },
  status_open:                 { en: "Open",                         hi: "Open" },
  status_completed:            { en: "Completed",                    hi: "Kaam Poora" },
  status_closed:               { en: "Closed",                       hi: "Band" },
  status_cancelled:            { en: "Cancelled",                    hi: "Cancel" },
  status_rework:               { en: "Rework",                       hi: "Dobara Kaam" },
};
